/**
 * The provider calendar's read model (GET /scheduling/provider-calendar): which facility each provider visits each
 * day of a week, built from eMerge visit plans. Read-only.
 */
import api from '../../services/api';
import type { ApiResult } from '../finance/financeApi';
import type { BuildingClosure } from '../provider/providerApi';
import type { RoundProgress } from '../provider/roundProgress';

export type CalendarVisitState = 'PLANNED' | 'CONFIRMED' | 'COMPLETED';

export interface CalendarVisit {
  plan_id: string;
  /** The day the visit is drawn on. */
  date: string;
  planned_on: string;
  state: CalendarVisitState;
  purpose: string | null;
  /** 'HH:MM' (24h, America/New_York) and the stop's place in the day's route; null when not set. */
  arrival_time?: string | null;
  route_order?: number | null;
  facility: { id: string; name: string | null; city: string | null; state: string | null };
  provider: { id: string; name: string | null } | null;
  building_closed: BuildingClosure | null;
  /** Today's round at this building (FR-2): counts only, never a resident; present for today's visits only. */
  round?: RoundProgress | null;
}

export interface CalendarLane {
  /** The employee id, or 'none' for the "No provider" lane. */
  key: string;
  provider: { id: string; name: string | null } | null;
  days: Record<string, CalendarVisit[]>;
  visits: number;
}

export interface ProviderCalendarPage {
  source: 'EMERGE';
  today: string;
  week: { from: string; through: string; days: string[]; prev: string; next: string; this_week: string };
  filter: { provider: string | null };
  lanes: CalendarLane[];
  visits: { total: number; drawn: number; limit: number; truncated: boolean };
  upcoming: { items: CalendarVisit[]; total: number; limit: number };
  providers: { id: string; name: string | null }[];
  enterprise: boolean;
}

export const providerCalendarApi = {
  getProviderCalendar: async (p: { week?: string | null; provider?: string | null }): Promise<ApiResult<ProviderCalendarPage>> => {
    const qp = new URLSearchParams();
    if (p.week) qp.set('week', p.week);
    if (p.provider) qp.set('provider', p.provider);
    const qs = qp.toString();
    return (await api.get(`/scheduling/provider-calendar${qs ? `?${qs}` : ''}`)) as ApiResult<ProviderCalendarPage>;
  },
};

export const getProviderCalendar = providerCalendarApi.getProviderCalendar;
